// SUB-OFFICE 7 — FORM 5-J, CONCURRENCE DUTY. For one shift you ARE the
// Bureau: a tray of strangers' real ledger entries, stamped blind, with the
// Bureau's stored stamp printing beside yours the instant you commit.
// Recorded: raw open/dwell/stamp events with hesitation. The agreement is
// computed server-side against stored verdict bands — zero inference.

import { api } from '../net';
import { holdSlamButton, printLayer, redacted } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';
import type { Form5JSpec } from '../../shared/physspec';
import type { LedgerEntry } from '../../shared/types';

function isApproveSide(stamp: string, toleratedIsApprove: boolean): boolean {
  return (
    stamp === 'APPROVED' ||
    stamp === 'APPROVED WITH DISAPPOINTMENT' ||
    (toleratedIsApprove && stamp === 'TOLERATED')
  );
}

/** Prefetch the public ledger so tray ids resolve to excerpts + stamps. */
async function ledgerMap(ids: string[]): Promise<Map<string, LedgerEntry>> {
  const map = new Map<string, LedgerEntry>();
  let cursor: number | null = 0;
  for (let page = 0; page < 5 && cursor !== null; page++) {
    const res = await api.record('ledger', cursor);
    if (!res.ok || !res.data) break;
    for (const e of res.data.entries as unknown as LedgerEntry[]) map.set(e.id, e);
    cursor = res.data.cursor;
    if (ids.every((id) => map.has(id))) break;
  }
  return map;
}

export async function runConcurrence(
  deps: BenchDeps,
  taskId: string,
  spec: Form5JSpec
): Promise<BehaviorLog | null> {
  const rec = new PhysLogRecorder('FORM5J', taskId);
  const entries = await ledgerMap(spec.tray);
  const layer = printLayer();

  const card = document.createElement('div');
  card.className = 'paper-modal wide';
  card.innerHTML =
    `<div class="paper-head letterhead"><span>FORM 5-J — CONCURRENCE DUTY</span><span class="tab-num">TRAY OF ${spec.tray.length}</span></div>`;
  const body = document.createElement('div');
  body.className = 'paper-body typewriter';
  card.appendChild(body);
  const foot = document.createElement('div');
  foot.className = 'paper-foot';
  card.appendChild(foot);
  layer.appendChild(card);

  const placard = document.createElement('div');
  placard.className = 'compose-note';
  placard.textContent = spec.placard_variant === 1
    ? 'THE BUREAU TOLERATES. TOLERATION IS ACCEPTANCE. STAMP AS THE BUREAU WOULD.'
    : 'STAMP AS THE BUREAU WOULD. THE BUREAU HAS ALREADY DECIDED. IT IS WATCHING YOUR INSTINCTS.';
  body.appendChild(placard);

  const zone = document.createElement('div');
  zone.style.cssText = 'margin-top:10px;min-height:180px';
  body.appendChild(zone);

  const streakLine = document.createElement('div');
  streakLine.className = 'compose-note tab-num';
  streakLine.style.marginTop = '8px';
  streakLine.textContent = 'CONCURRENCE STREAK: 0';
  body.appendChild(streakLine);

  let streak = 0;
  let abandoned = false;

  const abandonBtn = document.createElement('button');
  abandonBtn.type = 'button';
  abandonBtn.className = 'slam-btn secondary';
  abandonBtn.textContent = 'ABANDON THE TRAY';
  foot.appendChild(abandonBtn);

  const finish = (): void => {
    card.remove();
  };

  for (let idx = 0; idx < spec.tray.length; idx++) {
    if (abandoned) break;
    const id = spec.tray[idx];
    const entry = entries.get(id) || null;

    // eslint-disable-next-line no-await-in-loop
    const proceed = await new Promise<boolean>((resolve) => {
      zone.innerHTML = '';
      const openBtn = document.createElement('button');
      openBtn.type = 'button';
      openBtn.className = 'slam-btn';
      openBtn.textContent = `OPEN CANISTER ${idx + 1} OF ${spec.tray.length}`;
      zone.appendChild(openBtn);
      const onAbandon = (): void => {
        abandoned = true;
        resolve(false);
      };
      abandonBtn.onclick = onAbandon;

      openBtn.addEventListener('click', () => {
        deps.bus.emit('audio:flap');
        rec.ev('canister_opened', { idx, ledger_id: id });
        const unfurlAt = rec.now();
        zone.innerHTML = '';
        const paper = document.createElement('div');
        paper.style.cssText = 'border:1px solid var(--carbon);padding:10px 12px;background:var(--paper);font-size:13px;line-height:1.6';
        const head = document.createElement('div');
        head.className = 'field-label';
        head.textContent = entry ? `${entry.designation}${entry.seeded ? ' — RECOVERED, SUB-OFFICE 4 (CLOSED)' : ''}` : 'RECORD RETRIEVED FROM STORAGE';
        paper.appendChild(head);
        const ex = document.createElement('div');
        ex.appendChild(redacted(entry ? entry.excerpt : '[[R]]entry under retrieval[[/R]] — stamp on instinct; the Bureau will compare anyway.'));
        paper.appendChild(ex);
        zone.appendChild(paper);

        const pads = document.createElement('div');
        pads.style.cssText = 'display:flex;gap:14px;justify-content:center;margin-top:12px';
        zone.appendChild(pads);
        const counter = document.createElement('div');
        counter.style.cssText = 'text-align:center;margin-top:10px;min-height:40px';
        zone.appendChild(counter);

        let stamped = false;
        const slam = (verdict: 'APPROVE' | 'DENY') => (r: { heldMs: number }): void => {
          if (stamped) return;
          stamped = true;
          const hesitationMs = Math.max(1, rec.now() - unfurlAt);
          rec.ev('excerpt_dwell', { idx, ms: hesitationMs });
          rec.ev('stamp', { idx, verdict, holdMs: Math.round(r.heldMs), hesitationMs });
          deps.bus.emit('audio:thunk');
          // the Bureau's counter-stamp prints a beat later, with its reasoning
          window.setTimeout(() => {
            const stored = entry ? entry.stamp : null;
            const concur = stored !== null
              ? (verdict === 'APPROVE') === isApproveSide(stored, spec.tolerated_is_approve)
              : null;
            const line = document.createElement('div');
            line.className = 'typewriter';
            line.style.fontSize = '12px';
            line.innerHTML =
              `<span class="stamp ${concur === false ? 'stamp-red' : 'stamp-ink'}" style="font-size:12px;padding:3px 8px">THE BUREAU: ${stored || 'ON FILE'}</span>`;
            counter.appendChild(line);
            const verdictLine = document.createElement('div');
            verdictLine.style.cssText = 'font-size:11px;margin-top:4px;letter-spacing:.08em';
            if (concur === true) {
              streak++;
              rec.ev('streak', { len: streak });
              verdictLine.textContent = 'CONCURRENCE. THE WHEEL CLICKS UP.';
              deps.bus.emit('audio:flap');
            } else if (concur === false) {
              streak = 0;
              verdictLine.textContent = 'DISSENT. THE WHEEL RESETS. THE BUREAU HAS BEEN WRONG BEFORE, HISTORICALLY.';
              deps.bus.emit('audio:buzz');
            } else {
              verdictLine.textContent = 'THE COMPARISON WILL BE MADE IN THE BACK OFFICE.';
            }
            counter.appendChild(verdictLine);
            streakLine.textContent = `CONCURRENCE STREAK: ${streak}`;
            window.setTimeout(() => resolve(true), 1100);
          }, 420);
        };
        pads.appendChild(holdSlamButton('APPROVE-SIDE', slam('APPROVE')));
        pads.appendChild(holdSlamButton('DENY-SIDE', slam('DENY'), { className: 'red' }));
      });
    });
    if (!proceed) break;
  }

  finish();
  if (abandoned) return null;
  const built = rec.build(false);
  if (!built.valid) {
    deps.bus.emit('toast', `The tray log did not assemble (${built.reason}). The duty remains open.`);
    return null;
  }
  return built.log;
}
